/**
 * Especificações de papel e grid editorial.
 *
 * As margens seguem convenção de encadernação: a margem interna (do lado da
 * lombada) é maior que a externa, para o texto não sumir no vinco.
 */
import { limitar } from '../color/convert.js';
export const PAPEIS = {
    A4: {
        larguraMm: 210,
        alturaMm: 297,
        margemInternaMm: 20,
        margemExternaMm: 14,
        margemTopoMm: 15,
        margemBaseMm: 15,
        colunas: 12,
        gutterMm: 4,
        alturaLinhaPadraoMm: 7,
        corpoPt: 10.5,
        largo: true,
        estreito: false,
        quadrosNaFaixa: 5,
        sistemasDePartitura: 12,
        rotulo: 'A4 (210 × 297 mm)',
    },
    A5: {
        larguraMm: 148,
        alturaMm: 210,
        margemInternaMm: 16,
        margemExternaMm: 11,
        margemTopoMm: 12,
        margemBaseMm: 12,
        colunas: 8,
        gutterMm: 3.5,
        alturaLinhaPadraoMm: 6,
        corpoPt: 9.5,
        largo: false,
        estreito: false,
        quadrosNaFaixa: 4,
        sistemasDePartitura: 9,
        rotulo: 'A5 (148 × 210 mm)',
    },
    A6: {
        larguraMm: 105,
        alturaMm: 148,
        margemInternaMm: 12,
        margemExternaMm: 9,
        margemTopoMm: 9,
        margemBaseMm: 9,
        colunas: 6,
        gutterMm: 3,
        alturaLinhaPadraoMm: 5,
        corpoPt: 8.2,
        largo: false,
        estreito: true,
        quadrosNaFaixa: 3,
        sistemasDePartitura: 7,
        rotulo: 'A6 (105 × 148 mm)',
    },
    /*
     * Medidas de papelaria.
     *
     * O corpo de texto acompanha a **largura** da folha (é a largura que decide o
     * comprimento da linha), e não a altura. O Slim é estreito e comprido, o
     * Quadrado é curto e largo, e o Universitário é o mais alto de todos — cada um
     * com margem e grade próprias.
     */
    SLIM: {
        larguraMm: 110,
        alturaMm: 210,
        margemInternaMm: 12,
        margemExternaMm: 9,
        margemTopoMm: 10,
        margemBaseMm: 10,
        colunas: 6,
        gutterMm: 3,
        alturaLinhaPadraoMm: 5.2,
        corpoPt: 8.4,
        largo: false,
        estreito: true,
        quadrosNaFaixa: 3,
        sistemasDePartitura: 9,
        rotulo: 'Slim (110 × 210 mm)',
    },
    QUADRADO: {
        larguraMm: 180,
        alturaMm: 180,
        margemInternaMm: 16,
        margemExternaMm: 12,
        margemTopoMm: 12,
        margemBaseMm: 12,
        colunas: 10,
        gutterMm: 4,
        alturaLinhaPadraoMm: 6.4,
        corpoPt: 9.8,
        largo: false,
        estreito: false,
        quadrosNaFaixa: 5,
        sistemasDePartitura: 8,
        rotulo: 'Quadrado (180 × 180 mm)',
    },
    BIG: {
        larguraMm: 190,
        alturaMm: 240,
        margemInternaMm: 17,
        margemExternaMm: 12,
        margemTopoMm: 13,
        margemBaseMm: 13,
        colunas: 10,
        gutterMm: 4,
        alturaLinhaPadraoMm: 6.6,
        corpoPt: 10,
        largo: false,
        estreito: false,
        quadrosNaFaixa: 5,
        sistemasDePartitura: 10,
        rotulo: 'Big (190 × 240 mm)',
    },
    UNIVERSITARIO: {
        larguraMm: 185,
        alturaMm: 265,
        margemInternaMm: 17,
        margemExternaMm: 12,
        margemTopoMm: 14,
        margemBaseMm: 14,
        colunas: 10,
        gutterMm: 4,
        alturaLinhaPadraoMm: 6.6,
        corpoPt: 9.9,
        largo: false,
        estreito: false,
        quadrosNaFaixa: 5,
        sistemasDePartitura: 11,
        rotulo: 'Universitário (185 × 265 mm)',
    },
};
/** Multiplicadores relativos ao corpo de texto base. */
export const ESCALA_TEXTO = {
    XS: 0.76,
    SM: 0.87,
    MD: 1,
    LG: 1.28,
    XL: 1.72,
    DISPLAY: 2.9,
};
export const ENTRELINHA_PADRAO = {
    XS: 1.25,
    SM: 1.3,
    MD: 1.38,
    LG: 1.2,
    XL: 1.12,
    DISPLAY: 1,
};
/** Teto da sangria de corte, em milímetros. */
export const SANGRIA_MAXIMA_MM = 5;
/** Folga entre o conteúdo e o traço da moldura, em milímetros. */
export const FOLGA_DA_MOLDURA_MM = 4;
/**
 * Retângulo onde a moldura é desenhada.
 *
 * A moldura vive no espaço entre o conteúdo e a borda do papel: parte da caixa
 * do conteúdo e cresce para fora até a folga, sem nunca passar da folha — numa
 * página A6 com margem apertada, encostar na borda significaria sair no corte.
 *
 * A medida sai daqui, e não do componente, porque é geometria de papel: assim
 * ela é conferida em milímetros nos testes, sem navegador.
 *
 * Devolve `null` quando não há moldura a desenhar (sem moldura ou espaço de
 * menos para um quadro que não vire borrão).
 */
export function molduraDaPagina(medidas, tipo) {
    if (tipo === 'NENHUMA')
        return null;
    /*
     * A folga é aplicada lado a lado, e cada lado é limitado pela borda do papel.
     *
     * A conta não pode ser "conteúdo + folga de cada lado": com margem de lombada
     * o conteúdo não fica centrado, e um quadro centralizado cortava a largura do
     * conteúdo do lado do vinco. Assim o traço nunca entra no conteúdo e nunca
     * passa da folha — mesmo que o quadro fique mais largo do lado de fora.
     */
    const xMm = Math.max(2, medidas.conteudoXMm - FOLGA_DA_MOLDURA_MM);
    const yMm = Math.max(2, medidas.conteudoYMm - FOLGA_DA_MOLDURA_MM);
    /*
     * O limite de fora é a folha **aparada**, não a folha com sangria: a sobra da
     * sangria existe para ser cortada, e um traço desenhado nela sairia pela
     * metade depois da guilhotina.
     *
     * A sangria é lida com fallback porque `molduraDaPagina` também é chamada com
     * medidas montadas à mão (é geometria pura, e os testes conferem a conta).
     */
    const sangria = medidas.sangriaMm ?? 0;
    const larguraAparada = medidas.larguraAparadaMm ?? medidas.larguraMm - sangria * 2;
    const alturaAparada = medidas.alturaAparadaMm ?? medidas.alturaMm - sangria * 2;
    const limiteDireito = sangria + larguraAparada - 2;
    const limiteBase = sangria + alturaAparada - 2;
    const direitaMm = Math.min(limiteDireito, medidas.conteudoXMm + medidas.larguraConteudoMm + FOLGA_DA_MOLDURA_MM);
    const baseMm = Math.min(limiteBase, medidas.conteudoYMm + medidas.alturaConteudoMm + FOLGA_DA_MOLDURA_MM);
    const larguraMm = direitaMm - xMm;
    const alturaMm = baseMm - yMm;
    // Abaixo disso o quadro vira um borrão no meio da folha: melhor não desenhar.
    if (larguraMm < 20 || alturaMm < 20)
        return null;
    return { xMm, yMm, larguraMm, alturaMm };
}
/**
 * Lado da lombada nesta página, já considerando a escolha do miolo.
 *
 * Empate é resolvido pela convenção do livro: página ímpar com a lombada à
 * esquerda, página par à direita.
 */
export function ladoEfetivo(papel, indicePar) {
    const escolha = papel?.ladoLombada ?? 'ESPELHADA';
    if (escolha === 'DIREITA')
        return 'DIREITO';
    if (escolha === 'ESQUERDA')
        return 'ESQUERDO';
    return indicePar ? 'DIREITO' : 'ESQUERDO';
}
/** Tamanho final da folha, já virando para paisagem quando for o caso. */
export function dimensoesDaFolha(formato, orientacao = 'RETRATO') {
    const papel = PAPEIS[formato];
    return orientacao === 'PAISAGEM'
        ? { larguraMm: papel.alturaMm, alturaMm: papel.larguraMm }
        : { larguraMm: papel.larguraMm, alturaMm: papel.alturaMm };
}
/** Rótulo do papel com orientação, para a interface e o resumo. */
export function rotuloDaFolha(formato, orientacao = 'RETRATO') {
    const { larguraMm, alturaMm } = dimensoesDaFolha(formato, orientacao);
    return `${formato} (${larguraMm} × ${alturaMm} mm)`;
}
/**
 * Mede a área útil da página.
 *
 * Aceita tanto a assinatura antiga (`medirPagina('A5', 'ESQUERDO')`) quanto as
 * opções completas. A orientação troca largura e altura; a lombada decide onde
 * entra a margem maior; e a margem extra substitui a interna padrão do papel.
 */
export function medirPagina(formato, opcoes = {}) {
    const o = typeof opcoes === 'string' ? { lado: opcoes } : opcoes;
    const papel = PAPEIS[formato];
    const { larguraMm, alturaMm } = dimensoesDaFolha(formato, o.orientacao ?? 'RETRATO');
    const sangria = Math.max(0, Math.min(o.sangriaMm ?? 0, SANGRIA_MAXIMA_MM));
    const lombada = o.ladoLombada ?? 'ESPELHADA';
    const lado = o.lado ?? 'ESQUERDO';
    const margemInterna = o.margemLombadaMm && o.margemLombadaMm > 0 ? o.margemLombadaMm : papel.margemInternaMm;
    let inicio = papel.margemExternaMm;
    let fim = papel.margemExternaMm;
    let topo = papel.margemTopoMm;
    let base = papel.margemBaseMm;
    switch (lombada) {
        case 'NENHUMA':
            // Folha simétrica: nada de margem maior de um lado.
            break;
        case 'TOPO':
            topo = margemInterna;
            break;
        case 'DIREITA':
            fim = margemInterna;
            break;
        case 'ESQUERDA':
            inicio = margemInterna;
            break;
        case 'ESPELHADA':
        default:
            if (lado === 'ESQUERDO') {
                inicio = margemInterna;
            }
            else {
                fim = margemInterna;
            }
            break;
    }
    const larguraConteudoMm = larguraMm - inicio - fim;
    const alturaConteudoMm = alturaMm - topo - base;
    const larguraColunaMm = (larguraConteudoMm - papel.gutterMm * (papel.colunas - 1)) / papel.colunas;
    return {
        /*
         * A folha que sai no arquivo tem a sangria somada dos dois lados; a área de
         * escrita é medida a partir da folha **aparada**, então ela não muda de
         * tamanho — só desloca, para o desenho começar depois da linha do corte.
         */
        larguraMm: larguraMm + sangria * 2,
        alturaMm: alturaMm + sangria * 2,
        conteudoXMm: inicio + sangria,
        conteudoYMm: topo + sangria,
        larguraConteudoMm,
        alturaConteudoMm,
        larguraColunaMm,
        sangriaMm: sangria,
        larguraAparadaMm: larguraMm,
        alturaAparadaMm: alturaMm,
    };
}
export function tamanhoFontePt(formato, tamanho, escala = 1, tracking = 0) {
    const papel = PAPEIS[formato];
    const base = papel.corpoPt * ESCALA_TEXTO[tamanho] * limitar(escala, 0.7, 1.4);
    // Títulos com tracking maior ganham um leve aumento de corpo para manter peso.
    const ajuste = 1 + Math.min(tracking, 0.3) * 0.08;
    return Number((base * ajuste).toFixed(2));
}
/**
 * Mede a página já considerando a configuração do miolo.
 *
 * É o que o motor e o renderizador devem usar: orientação, posição da lombada e
 * margem escolhida vivem na configuração, não no formato do papel.
 */
export function medirPaginaDoMiolo(config, indicePar = false) {
    return medirPagina(config.formato, {
        orientacao: config.papel?.orientacao ?? 'RETRATO',
        ladoLombada: config.papel?.ladoLombada ?? 'ESPELHADA',
        margemLombadaMm: config.papel?.margemLombadaMm ?? 0,
        lado: ladoEfetivo(config.papel, indicePar),
        sangriaMm: config.sangriaMm ?? 0,
    });
}
export function dimensoesDoMiolo(config) {
    const orientacao = config.papel?.orientacao ?? 'RETRATO';
    const { larguraMm, alturaMm } = dimensoesDaFolha(config.formato, orientacao);
    return { larguraMm, alturaMm, rotulo: rotuloDaFolha(config.formato, orientacao) };
}
/** Quantas linhas cabem em uma altura, dado o passo da pauta. */
export function contagemLinhas(alturaMm, alturaLinhaMm, espacamentoMm) {
    const passo = alturaLinhaMm + espacamentoMm;
    if (passo <= 0)
        return 0;
    return Math.max(0, Math.floor((alturaMm + espacamentoMm) / passo));
}
/** Pauta ideal (altura + espaço) por formato, considerando a fonte base. */
export function pautaSugerida(formato, fator = 1) {
    const papel = PAPEIS[formato];
    const altura = Number((papel.alturaLinhaPadraoMm * fator).toFixed(2));
    return { alturaLinhaMm: altura, espacamentoMm: 0 };
}
/** Largura de uma faixa de colunas do grid modular. */
export function larguraColunas(formato, quantidade) {
    const papel = PAPEIS[formato];
    const medidas = medirPagina(formato);
    const colunas = limitar(quantidade, 1, papel.colunas);
    return colunas * medidas.larguraColunaMm + (colunas - 1) * papel.gutterMm;
}
//# sourceMappingURL=papel.js.map